import { Lightbulb } from "lucide-react";
import Select from "react-select";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GetCoachingProgramsQuery } from "@/graphql";
import { Specialty } from "@/types/products";
import { SelectFn, SelectOption } from "@/types/select";

type Program = GetCoachingProgramsQuery["coachingPrograms"]["programs"][number];
type Product = Program["sessions"][number]["product"];
type CoachProduct = Program["coaches"][number]["coachProduct"];

interface ProductSelectorProps {
  specialties: Specialty[];
  coachProducts: CoachProduct[];
  selectedProduct: Product | null;
  selectedSpecialtyId: string | null;
  onSelectProduct: (product: Product, coachProduct: CoachProduct) => void;
  onSelectSpecialtyId: (specialtyId: string) => void;
  loading: boolean;
}

export function ProductSelector({
  specialties,
  coachProducts,
  selectedProduct,
  selectedSpecialtyId,
  onSelectProduct,
  onSelectSpecialtyId,
  loading,
}: ProductSelectorProps) {
  const selectedSpecialty = specialties.find(
    (s) => s.specialtyId === selectedSpecialtyId,
  );

  const specialtyOptions: SelectOption[] = specialties.map((p) => ({
    value: p.specialtyId,
    label: p.name,
  }));

  const onSelectSpecialtyOption: SelectFn<SelectOption> = (option) => {
    const specialty = specialties.find((p) => p.specialtyId === option?.value);
    const coachProduct = coachProducts.find(
      (cp) => cp.productId === specialty?.products[0].id,
    );
    if (specialty && coachProduct) {
      onSelectSpecialtyId(specialty.specialtyId);
      onSelectProduct(specialty.products[0], coachProduct);
    }
  };

  if (specialties.length === 0 && !loading) {
    return (
      <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-4">
        <Lightbulb className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-700 font-normal">
          <strong>Heads up</strong> – There are no bookable sessions available
          for this coach.
        </div>
      </div>
    );
  }

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle>Choose session type</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Specialty */}
        <div className="flex flex-col gap-2">
          <div className="text-sm">Coaching Type</div>
          <Select
            value={specialtyOptions.find(
              (o) => o.value === selectedSpecialtyId,
            )}
            onChange={onSelectSpecialtyOption}
            isLoading={loading}
            isSearchable={true}
            options={specialtyOptions}
            className="font-normal ml-1"
          />
        </div>

        {/* Product */}
        <div className="flex flex-col gap-2">
          <div className="text-sm">Session Type</div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 ml-1">
              {[...Array(2)].map((_, index) => (
                <div
                  key={index}
                  className="h-16 animate-pulse rounded-lg bg-gray-300"
                />
              ))}
            </div>
          ) : (
            selectedSpecialty?.products.length && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 ml-1">
                {selectedSpecialty.products.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => {
                      const coachProduct = coachProducts.find(
                        (cp) => cp.productId === product.id,
                      );
                      if (coachProduct) {
                        onSelectProduct(product, coachProduct);
                      }
                    }}
                    className={`
                      px-6 py-4 rounded-lg border-2 text-center font-medium transition-colors
                      ${
                        selectedProduct?.id === product.id
                          ? "border-blue-500 bg-blue-50 text-blue-600"
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                      }
                    `}
                  >
                    {product.name}
                  </button>
                ))}
              </div>
            )
          )}
        </div>
      </CardContent>
    </Card>
  );
}
